/**
 * useEditorState — manages all WYSIWYG puzzle editor state.
 *
 * The state is a PuzzleJSON object plus metadata about the loaded puzzle.
 * All mutations produce new immutable PuzzleJSON values.
 */

import { useState, useCallback } from 'react';
import type { PuzzleJSON } from '../../types';
import { createBlankPuzzle } from '../../types/editor.types';
import { importTextToPuzzleJSON, puzzleJSONToText, parsePuzzleJSON, puzzleDataToJSON } from '../../utils/puzzleParser';
import { confirmUnsaved } from './confirmUnsaved';

export interface EditorState {
  puzzle: PuzzleJSON;
  isDirty: boolean;
  activePuzzleId: string | null;
  activePuzzleDate: string | null;
  activePuzzleIsSample: boolean;
}

export interface EditorActions {
  setClueText: (col: number, pair: number, position: 0 | 1, text: string) => void;
  setSolution: (text: string) => void;
  toggleHidden: (col: number, pair: number) => boolean; // returns false if user cancelled
  setCategory: (col: number, text: string) => void;
  setJoiner: (col: number, pair: number, text: string | null) => void;
  setAlternates: (col: number, alternates: string[]) => void;
  setSolutionAlternates: (alternates: string[]) => void;
  importFromText: (text: string) => void;
  loadFromSupabase: (id: string) => Promise<void>;
  saveToSupabase: () => Promise<boolean>;
  newPuzzle: () => Promise<void>;
  exportJSON: () => string;
  setDisplayHint: (
    section: 'clues' | 'recap',
    key: string,
    hints: { lineBreaks?: number[]; hyphens?: number[] } | null
  ) => void;
  setIsSample: (isSample: boolean) => void;
  reorderColumns: (fromIndex: number, toIndex: number) => void;
  flipColumn: (col: number) => void;
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function supabaseFetch(path: string, options?: RequestInit) {
  return fetch(`${supabaseUrl}${path}`, {
    ...options,
    headers: {
      'apikey': supabaseKey,
      'Authorization': `Bearer ${supabaseKey}`,
      'Content-Type': 'application/json',
      'Prefer': 'return=minimal',
      ...(options?.headers ?? {}),
    },
  });
}

export function useEditorState(): [EditorState, EditorActions] {
  const [puzzle, setPuzzle] = useState<PuzzleJSON>(() => createBlankPuzzle());
  const [isDirty, setIsDirty] = useState(false);
  const [activePuzzleId, setActivePuzzleId] = useState<string | null>(null);
  const [activePuzzleDate, setActivePuzzleDate] = useState<string | null>(null);
  const [activePuzzleIsSample, setActivePuzzleIsSample] = useState(false);

  const update = useCallback((fn: (p: PuzzleJSON) => PuzzleJSON) => {
    setPuzzle(prev => fn(prev));
    setIsDirty(true);
  }, []);

  const setClueText = useCallback((col: number, pair: number, position: 0 | 1, text: string) => {
    update(p => {
      const columns = [...p.columns];
      const colDef = { ...columns[col], pairs: [...columns[col].pairs] };
      const pairDef = { ...colDef.pairs[pair], clues: [...colDef.pairs[pair].clues] as [string, string] };
      pairDef.clues[position] = text;
      colDef.pairs[pair] = pairDef;
      columns[col] = colDef;
      return { ...p, columns };
    });
  }, [update]);

  const setSolution = useCallback((text: string) => {
    update(p => ({ ...p, solution: { ...p.solution, primary: text } }));
  }, [update]);

  const toggleHidden = useCallback((col: number, pair: number): boolean => {
    // If toggling pair 2 (guess pair) and alternates exist, warn
    const colDef = puzzle.columns[col];
    if (pair === 2 && colDef.answerAlternates.length > 0) {
      const confirmed = window.confirm(
        'Toggling the hidden clue on the guess pair will clear the column answer alternates. Continue?'
      );
      if (!confirmed) return false;
    }

    update(p => {
      const columns = [...p.columns];
      const col_ = { ...columns[col], pairs: [...columns[col].pairs] };
      const pair_ = { ...col_.pairs[pair] };
      pair_.hiddenIndex = pair_.hiddenIndex === 0 ? 1 : 0;
      col_.pairs[pair] = pair_;
      if (pair === 2) {
        col_.answerAlternates = [];
      }
      columns[col] = col_;
      return { ...p, columns };
    });
    return true;
  }, [puzzle, update]);

  const setCategory = useCallback((col: number, text: string) => {
    update(p => {
      const columns = [...p.columns];
      columns[col] = { ...columns[col], category: text };
      return { ...p, columns };
    });
  }, [update]);

  const setJoiner = useCallback((col: number, pair: number, text: string | null) => {
    update(p => {
      const columns = [...p.columns];
      const colDef = { ...columns[col], pairs: [...columns[col].pairs] };
      colDef.pairs[pair] = { ...colDef.pairs[pair], joiner: text };
      columns[col] = colDef;
      return { ...p, columns };
    });
  }, [update]);

  const setAlternates = useCallback((col: number, alternates: string[]) => {
    update(p => {
      const columns = [...p.columns];
      columns[col] = { ...columns[col], answerAlternates: alternates };
      return { ...p, columns };
    });
  }, [update]);

  const setSolutionAlternates = useCallback((alternates: string[]) => {
    update(p => ({ ...p, solution: { ...p.solution, alternates } }));
  }, [update]);

  const importFromText = useCallback((text: string) => {
    try {
      const imported = importTextToPuzzleJSON(text);
      setPuzzle(imported);
      setIsDirty(true);
      setActivePuzzleId(null);
      setActivePuzzleDate(null);
      setActivePuzzleIsSample(false);
    } catch (err) {
      alert(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, []);

  const loadFromSupabase = useCallback(async (id: string) => {
    const res = await supabaseFetch(
      `/rest/v1/puzzles_admin?id=eq.${id}&select=id,puzzle_text,puzzle_json,date,is_sample`
    );
    if (!res.ok) {
      alert('Failed to load puzzle');
      return;
    }
    const rows = await res.json();
    if (rows.length === 0) {
      alert('Puzzle not found');
      return;
    }
    const row = rows[0];
    let loaded: PuzzleJSON;
    if (row.puzzle_json) {
      loaded = row.puzzle_json;
    } else {
      // Convert from text format
      const pd = parsePuzzleJSON(
        puzzleDataToJSON(
          // We need to parse the text first
          (await import('../../utils/puzzleParser')).parsePuzzle(row.puzzle_text, row.date)
        ),
        row.date
      );
      loaded = puzzleDataToJSON(pd);
    }
    setPuzzle(loaded);
    setIsDirty(false);
    setActivePuzzleId(row.id);
    setActivePuzzleDate(row.date);
    setActivePuzzleIsSample(row.is_sample ?? false);
  }, []);

  const saveToSupabase = useCallback(async (): Promise<boolean> => {
    const textVersion = puzzleJSONToText(puzzle);
    // 'name' column is NOT NULL — keep it in sync with the solution
    const name = puzzle.solution.primary || 'Untitled';

    if (activePuzzleId) {
      // UPDATE existing puzzle
      const res = await supabaseFetch(
        `/rest/v1/puzzles_admin?id=eq.${activePuzzleId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            name,
            puzzle_json: puzzle,
            puzzle_text: textVersion,
          }),
        }
      );
      if (!res.ok) {
        alert('Failed to save puzzle');
        return false;
      }
    } else {
      // INSERT new puzzle
      const res = await supabaseFetch(
        '/rest/v1/puzzles_admin',
        {
          method: 'POST',
          headers: { 'Prefer': 'return=representation' },
          body: JSON.stringify({
            name,
            puzzle_json: puzzle,
            puzzle_text: textVersion,
          }),
        }
      );
      if (!res.ok) {
        alert('Failed to create puzzle');
        return false;
      }
      const rows = await res.json();
      if (rows.length > 0) {
        setActivePuzzleId(rows[0].id);
        setActivePuzzleDate(rows[0].date);
      }
    }

    setIsDirty(false);
    return true;
  }, [activePuzzleId, puzzle]);

  const newPuzzle = useCallback(async () => {
    if (isDirty) {
      const choice = await confirmUnsaved('You have unsaved changes.');
      if (choice === 'cancel') return;
      if (choice === 'save') {
        const saved = await saveToSupabase();
        if (!saved) return;
      }
    }
    setPuzzle(createBlankPuzzle());
    setIsDirty(false);
    setActivePuzzleId(null);
    setActivePuzzleDate(null);
    setActivePuzzleIsSample(false);
  }, [isDirty, saveToSupabase]);

  const exportJSON = useCallback((): string => {
    return JSON.stringify(puzzle, null, 2);
  }, [puzzle]);

  const setIsSample = useCallback((isSample: boolean) => {
    setActivePuzzleIsSample(isSample);
  }, []);

  const reorderColumns = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    update(p => {
      const columns = [...p.columns];
      const [moved] = columns.splice(fromIndex, 1);
      columns.splice(toIndex, 0, moved);

      // Remap displayHints keys: "col-row" → new col index
      const displayHints = { ...p.displayHints };
      for (const section of ['clues', 'recap'] as const) {
        const old = displayHints[section];
        if (!old || Object.keys(old).length === 0) continue;
        const remapped: Record<string, { lineBreaks?: number[]; hyphens?: number[] }> = {};
        // Build old→new index mapping
        const mapping: number[] = [];
        for (let i = 0; i < 4; i++) {
          if (i === fromIndex) continue;
          mapping.push(i);
        }
        mapping.splice(toIndex, 0, fromIndex);
        // mapping[newIdx] = oldIdx, we need oldIdx→newIdx
        const oldToNew: Record<number, number> = {};
        for (let n = 0; n < mapping.length; n++) oldToNew[mapping[n]] = n;

        for (const [key, val] of Object.entries(old)) {
          const dash = key.indexOf('-');
          if (dash === -1) { remapped[key] = val; continue; }
          const colStr = key.slice(0, dash);
          const rest = key.slice(dash);
          const colNum = parseInt(colStr, 10);
          if (isNaN(colNum) || oldToNew[colNum] === undefined) { remapped[key] = val; continue; }
          remapped[`${oldToNew[colNum]}${rest}`] = val;
        }
        displayHints[section] = remapped;
      }

      return { ...p, columns, displayHints };
    });
  }, [update]);

  const flipColumn = useCallback((col: number) => {
    update(p => {
      const columns = [...p.columns];
      const colDef = { ...columns[col], pairs: [...columns[col].pairs] };
      colDef.pairs = colDef.pairs.map(pair => ({
        ...pair,
        clues: [pair.clues[1], pair.clues[0]] as [string, string],
        hiddenIndex: (pair.hiddenIndex === 0 ? 1 : 0) as 0 | 1,
      }));
      columns[col] = colDef;
      return { ...p, columns };
    });
  }, [update]);

  const setDisplayHint = useCallback((
    section: 'clues' | 'recap',
    key: string,
    hints: { lineBreaks?: number[]; hyphens?: number[] } | null
  ) => {
    update(p => {
      const displayHints = { ...p.displayHints };
      const sectionHints = { ...(displayHints[section] ?? {}) };
      if (hints === null) {
        delete sectionHints[key];
      } else {
        sectionHints[key] = hints;
      }
      displayHints[section] = sectionHints;
      return { ...p, displayHints };
    });
  }, [update]);

  return [
    { puzzle, isDirty, activePuzzleId, activePuzzleDate, activePuzzleIsSample },
    {
      setClueText, setSolution, toggleHidden, setCategory, setJoiner,
      setAlternates, setSolutionAlternates, importFromText, loadFromSupabase,
      saveToSupabase, newPuzzle, exportJSON, setDisplayHint, setIsSample,
      reorderColumns, flipColumn,
    },
  ];
}
